import React, { useState } from 'react'
import type { GiftCardRecipient } from 'api/api.types'
import { formatPhone } from 'utils/formatPhone'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { Modal } from 'components/Utility/Modal/Modal'
import { CertificateRecipientForm } from 'components/Forms/CertificateRecipientForm/CertificateRecipientForm'
import type { Cart } from './types'
import styles from './GiftCardsPage.module.scss'

type GiftCardsCartProps = {
  order: Cart,
  isSubmitting: boolean,
  onRemove: (index: number) => void,
  onIncreaseAmount: (index: number) => void,
  onReduceAmount: (index: number) => void,
  onAddMore: () => void,
  onEditRecipient: (index: number, recipient: GiftCardRecipient) => void,
  onSubmit: () => void,
}

/** The cart: recipient, amount and sum per card, total and payment */
export const GiftCardsCart: React.FC<GiftCardsCartProps> = (props) => {
  const { order, isSubmitting, onAddMore, onSubmit } = props
  const { onRemove, onIncreaseAmount, onReduceAmount, onEditRecipient } = props
  const [editRecipientIndex, setEditRecipientIndex] = useState<number | null>(null)

  if (!order.items.length) {
    return (
      <div className={styles.orderEmpty}>
        <div className={styles.orderEmptyTitle}>
          В корзине пока ничего нет
        </div>
        <AppButton onClick={onAddMore}>
          Добавить карту
        </AppButton>
      </div>
    )
  }

  const total = order.items.reduce((acc, item) => acc + item.certificate.sum * item.amount, 0) - order.discount

  return (
    <div className={styles.order}>
      <div className={styles.cart}>
        <div className={styles.cartHeader}>
          <div className={styles.cartRow}>
            <div data-name={'image'} className={styles.cartCell} />
            <div data-name={'recipient'} className={styles.cartCell}>
              Получатель
            </div>
            <div data-name={'amount'} className={styles.cartCell}>
              Кол-во
            </div>
            <div data-name={'sum'} className={styles.cartCell}>
              Сумма
            </div>
            <div data-name={'actions'} className={styles.cartCell} />
          </div>
        </div>
        <div className={styles.cartContent}>
          {order.items.map((item, index) => (
            <div key={index} className={styles.cartRow}>
              <div data-name={'image'} className={styles.cartCell}>
                <img className={styles.cartImage} alt={item.certificate.name} src={item.certificate.image} />
              </div>
              <div data-name={'recipient'} className={styles.cartCell}>
                <div className={styles.cartRecipient}>
                  <div className={styles.cartRecipientInfo}>
                    <div className={styles.cartRecipientProp}>{item.recipient.name}</div>
                    <div className={styles.cartRecipientProp}>{item.recipient.email}</div>
                    <div className={styles.cartRecipientProp}>{formatPhone(item.recipient.phone)}</div>
                  </div>
                  <button onClick={() => setEditRecipientIndex(index)} className={styles.recipientChange} />
                </div>
              </div>
              <div data-name={'amount'} className={styles.cartCell}>
                <div className={styles.cartAmount}>
                  <button onClick={() => onReduceAmount(index)} className={styles.amountReduce} />
                  <div className={styles.amount}>{item.amount}</div>
                  <button onClick={() => onIncreaseAmount(index)} className={styles.amountIncrease} />
                </div>
              </div>
              <div data-name={'sum'} className={styles.cartCell}>
                {item.certificate.sum * item.amount} ₽
              </div>
              <div data-name={'actions'} className={styles.cartCell}>
                <button onClick={() => onRemove(index)} className={styles.cartItemRemove} />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.cartSummary}>
          <AppButton className={styles.cartAddMore} onClick={onAddMore}>Добавить еще карту</AppButton>
          <div className={styles.cartTotal}>
            Итого: {total} ₽
          </div>
          <AppButton className={styles.cartPayment} shadowColor={'pink'} disabled={isSubmitting} onClick={onSubmit}>
            Оплатить
          </AppButton>
        </div>
      </div>
      {editRecipientIndex !== null && (
        <Modal isOpen onClose={() => setEditRecipientIndex(null)}>
          <CertificateRecipientForm
            data={order.items[editRecipientIndex].recipient}
            onSubmit={(recipient) => {
              onEditRecipient(editRecipientIndex, recipient)
              setEditRecipientIndex(null)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
